import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

/**
 * ForgotPasswordPage
 *
 * Step 1: User enters email
 * Step 2: Backend generates a reset token (logged to console in dev,
 *         sent via email in production)
 * Step 3: UI shows a confirmation screen regardless of whether the
 *         email exists (OWASP — no account enumeration)
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await axios.post(`${USER_API}/api/auth/forgot-password`, {
        email: email.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      // Even on error, show success — OWASP says don't reveal if the email exists
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-xl font-body text-sm focus:outline-none focus:ring-2 transition-shadow";
  const inputStyle = {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "hsl(var(--foreground))",
    "--tw-ring-color": "var(--vb-blue)",
  };

  return (
    <>
      <Navbar />
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          paddingTop: "calc(var(--header-h, 80px) + 40px)",
          paddingBottom: 60,
        }}
      >
        <div className="w-full max-w-md">
          <div
            className="rounded-3xl border bg-card p-8 md:p-10"
            style={{
              borderColor: "rgba(255,255,255,0.08)",
              boxShadow: "var(--glow-card)",
            }}
          >
            {submitted ? (
              /* ── Success state ── */
              <div className="flex flex-col items-center text-center gap-5 py-4">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "rgba(91,140,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle2
                    className="h-7 w-7"
                    style={{ color: "var(--vb-blue)" }}
                  />
                </div>

                <div>
                  <h1
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 700,
                      fontSize: 24,
                      color: "hsl(var(--foreground))",
                      marginBottom: 8,
                    }}
                  >
                    Check your email
                  </h1>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    If an account exists for{" "}
                    <span
                      style={{
                        color: "var(--vb-blue)",
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 12,
                      }}
                    >
                      {email}
                    </span>
                    , we've sent a password reset link. It expires in 1 hour.
                  </p>
                </div>

                <p
                  className="font-body text-xs text-muted-foreground"
                  style={{ marginTop: 8 }}
                >
                  Didn't receive it? Check your spam folder or{" "}
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setEmail("");
                    }}
                    style={{
                      color: "var(--vb-blue)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                      padding: 0,
                    }}
                  >
                    try again
                  </button>
                </p>

                <Link
                  to="/auth"
                  className="font-body text-sm font-semibold flex items-center gap-1.5 mt-2"
                  style={{
                    color: "var(--vb-blue)",
                    textDecoration: "none",
                  }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              /* ── Email entry state ── */
              <>
                <div className="text-center mb-8">
                  <h1
                    style={{
                      fontFamily: "'Oswald', sans-serif",
                      fontWeight: 700,
                      fontSize: 28,
                      color: "hsl(var(--foreground))",
                      marginBottom: 6,
                    }}
                  >
                    Reset Password
                  </h1>
                  <p className="font-body text-sm text-muted-foreground">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                {error && (
                  <div
                    className="mb-5 px-4 py-3 rounded-xl text-sm font-body"
                    style={{
                      background: "rgba(239,68,68,0.08)",
                      color: "#f87171",
                      border: "1px solid rgba(239,68,68,0.2)",
                    }}
                  >
                    {error}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label className="font-body text-xs font-medium text-muted-foreground mb-1.5 block">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className={inputCls}
                      style={inputStyle}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center"
                    style={{ height: 50, borderRadius: 12, fontSize: 15 }}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>

                <div className="text-center mt-6">
                  <Link
                    to="/auth"
                    className="font-body text-sm font-medium flex items-center justify-center gap-1.5"
                    style={{
                      color: "var(--vb-blue)",
                      textDecoration: "none",
                    }}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
